/**
 * Scrolling to an element inside the app without moving the page around it.
 *
 * `element.scrollIntoView()` (and `#anchor` links) scroll every scrollable ancestor, the page
 * itself included. The app fills the window, so when the page moves the app slides up and a blank
 * band appears below it. These helpers scroll only the element's own scrolling container.
 */

function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    if ((overflowY === "auto" || overflowY === "scroll") && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}

export function scrollWithin(el: HTMLElement | null | undefined, opts: { block?: "start" | "center" | "end" | "nearest"; behavior?: ScrollBehavior; offset?: number } = {}) {
  if (!el) return;
  const box = scrollParent(el);
  if (!box) return;
  const { block = "start", behavior = "smooth", offset = 12 } = opts;
  const b = box.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  const rel = r.top - b.top + box.scrollTop;
  let top: number;
  if (block === "center") top = rel - (box.clientHeight - r.height) / 2;
  else if (block === "end") top = rel - box.clientHeight + r.height + offset;
  else if (block === "nearest") {
    if (r.top >= b.top && r.bottom <= b.bottom) return;
    top = r.top < b.top ? rel - offset : rel - box.clientHeight + r.height + offset;
  } else top = rel - offset;
  box.scrollTo({ top: Math.max(0, top), behavior });
}

export function scrollToId(id: string, opts?: Parameters<typeof scrollWithin>[1]) {
  scrollWithin(document.getElementById(id), opts);
}
